import {
  FocusEvent,
  useState,
  useMemo,
  useEffect,
  SyntheticEvent,
  ChangeEvent,
} from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormHelperText,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { GenericResource, ListResponse, QueryParams } from 'types/api';
import { useController } from 'react-hook-form';
import { QueryDefinition, skipToken } from '@reduxjs/toolkit/dist/query/react';
import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { HookFormComponentProps } from 'ui-component/HookFormComponents/types';
import { debounce } from 'lodash';
import { IconSparkles } from '@tabler/icons-react';
import { useTheme } from '@mui/material/styles';

/**
 * HookFormMultiQuerySingleFieldSearchSelect is a simplified version of HookQuerySearchSelect
 * that allows selecting string values from a specified field of objects
 * returned by multiple queries, rather than working with the full objects.
 *
 * @example
 * <HookFormMultiQuerySingleFieldSearchSelect
 *   label="Location"
 *   name="location"
 *   queryObjects={[
 *     {
 *       useGetQuery: useGetCountriesQuery,
 *       searchSchema: ['name'],
 *       schema: ['name']
 *     },
 *     {
 *       useGetQuery: useGetCitiesQuery,
 *       searchSchema: ['name'],
 *       schema: ['name']
 *     }
 *   ]}
 *   filters1={[{ field: 'region', operator: 'equals', value: 'Europe' }]}
 *   filters2={[{ field: 'country', operator: 'equals', value: 'France' }]}
 *   errors={errors}
 *   control={control}
 *   fieldName="name"
 *   freeSolo
 * />
 */

type FreeSoloStringProps = {
  /** Accept free user input as a valid value */
  freeSolo: true;
};

type NotFreeSoloStringProps = {
  freeSolo?: false;
};

type QueryObject<T extends GenericResource> = {
  useGetQuery: UseQuery<
    QueryDefinition<
      QueryParams<T>,
      BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
      string,
      ListResponse<T>
    >
  >;
  searchSchema?: QueryParams<T>['schema'];
  schema: QueryParams<T>['schema'];
};

export type HookFormMultiQuerySingleFieldSearchSelectProps<
  T1 extends GenericResource,
  T2 extends GenericResource
> = HookFormComponentProps & {
  name: string;
  disabled?: boolean;
  mb?: number;
  queryObjects: [QueryObject<T1>, QueryObject<T2>];
  filters1?: QueryParams<T1>['filters'];
  filters2?: QueryParams<T2>['filters'];
  excludes1?: QueryParams<T1>['excludes'];
  excludes2?: QueryParams<T2>['excludes'];
  /** Field name to extract from the object for string value - must be present in both resource types */
  fieldName: keyof T1 & keyof T2;
  blended?: boolean;
  fullWidth?: boolean;
  disableClearable?: boolean;
  hideInitialIfMoreOptions?: boolean;
  getOptionLabel?: (option: string) => string;
  /** Optional callback that receives the full selected object */
  /** This is for handling side effects when the value changes */
  onSelectionChange?: (selectedObject: T1 | T2 | null) => void;
  showAiIcon?: boolean;
  additionalOnBlur?: (
    e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
} & (FreeSoloStringProps | NotFreeSoloStringProps) &
  TextFieldProps;

const filter = createFilterOptions<string>();

export const HookFormMultiQuerySingleFieldSearchSelect = <
  T1 extends GenericResource,
  T2 extends GenericResource
>({
  errors,
  name,
  label,
  control,
  queryObjects,
  filters1 = [],
  filters2 = [],
  excludes1 = [],
  excludes2 = [],
  fieldName,
  disabled,
  sx,
  freeSolo = false,
  fullWidth = false,
  mb = 2,
  disableClearable = false,
  hideInitialIfMoreOptions = false,
  blended = false,
  getOptionLabel,
  onSelectionChange,
  showAiIcon = false,
  additionalOnBlur,
}: HookFormMultiQuerySingleFieldSearchSelectProps<T1, T2>) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [stringOptions, setStringOptions] = useState<string[]>([]);
  const [userEnteredOption, setUserEnteredOption] = useState<string | null>(
    null
  );

  const {
    field: { ref, onChange, value, onBlur },
    fieldState: { isDirty },
  } = useController({
    name,
    control,
  });

  const debouncedSetSearchQuery = debounce((searchValue: string) => {
    setSearchQuery(searchValue);
  }, 300);

  // Initial value queries for both query objects
  const initialFilters1: QueryParams<T1>['filters'] = useMemo(
    () =>
      isDirty
        ? filters1
        : value
        ? [
            {
              field: fieldName as keyof T1,
              operator: 'isAnyOf',
              value: [value],
            },
            ...filters1,
          ]
        : filters1,
    [value, isDirty, filters1, fieldName]
  );

  const initialFilters2: QueryParams<T2>['filters'] = useMemo(
    () =>
      isDirty
        ? filters2
        : value
        ? [
            {
              field: fieldName as keyof T2,
              operator: 'isAnyOf',
              value: [value],
            },
            ...filters2,
          ]
        : filters2,
    [value, isDirty, filters2, fieldName]
  );

  const [query1, query2] = queryObjects;

  // Initial value queries
  const {
    data: { data: initialValueOptions1 = [] } = { data: [] },
    isFetching: isFetchingInitialValueOptions1,
  } = query1.useGetQuery(
    initialFilters1.length > 0
      ? {
          pageSize: 100,
          filters: initialFilters1,
          excludes: excludes1,
          schema: [
            fieldName as keyof T1,
            ...(Array.isArray(query1.schema) ? query1.schema : []),
          ],
          blended,
        }
      : skipToken
  );

  const {
    data: { data: initialValueOptions2 = [] } = { data: [] },
    isFetching: isFetchingInitialValueOptions2,
  } = query2.useGetQuery(
    initialFilters2.length > 0
      ? {
          pageSize: 100,
          filters: initialFilters2,
          excludes: excludes2,
          schema: [
            fieldName as keyof T2,
            ...(Array.isArray(query2.schema) ? query2.schema : []),
          ],
          blended,
        }
      : skipToken
  );

  // Search queries for both query objects
  const {
    data: { data: options1 = [] } = { data: [] },
    isFetching: isFetchingOptions1,
  } = query1.useGetQuery({
    filters: filters1,
    excludes: excludes1,
    schema: [
      fieldName as keyof T1,
      ...(Array.isArray(query1.schema) ? query1.schema : []),
    ],
    searchSchema: query1.searchSchema || query1.schema,
    search: searchQuery,
    blended,
  });

  const {
    data: { data: options2 = [] } = { data: [] },
    isFetching: isFetchingOptions2,
  } = query2.useGetQuery({
    filters: filters2,
    excludes: excludes2,
    schema: [
      fieldName as keyof T2,
      ...(Array.isArray(query2.schema) ? query2.schema : []),
    ],
    searchSchema: query2.searchSchema || query2.schema,
    search: searchQuery,
    blended,
  });

  useEffect(() => {
    const combinedData = [
      ...options1,
      ...options2,
      ...(hideInitialIfMoreOptions &&
      (options1.length > 0 || options2.length > 0)
        ? []
        : [...initialValueOptions1, ...initialValueOptions2]),
    ];

    const extractedValues = combinedData
      .filter((item) => item && item[fieldName] !== undefined)
      .map((item) => String(item[fieldName]));
    const uniqueValues = Array.from(new Set(extractedValues));

    const sortedValues = uniqueValues.sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();
      const searchLower = searchQuery?.toLowerCase() || '';

      if (aLower.startsWith(searchLower) && !bLower.startsWith(searchLower)) {
        return -1;
      }
      if (!aLower.startsWith(searchLower) && bLower.startsWith(searchLower)) {
        return 1;
      }
      return aLower.localeCompare(bLower);
    });

    if (
      sortedValues.length !== stringOptions.length ||
      sortedValues.some((val, index) => val !== stringOptions[index])
    ) {
      setStringOptions(sortedValues);
    }
  }, [
    options1,
    options2,
    initialValueOptions1,
    initialValueOptions2,
    fieldName,
    hideInitialIfMoreOptions,
    searchQuery,
    stringOptions,
  ]);

  const allOptions = useMemo(() => {
    if (userEnteredOption && !stringOptions.includes(userEnteredOption)) {
      return [...stringOptions, userEnteredOption];
    }
    return stringOptions;
  }, [stringOptions, userEnteredOption]);

  const handleChange = (event: SyntheticEvent, newValue: string | null) => {
    if (newValue === null) {
      onChange(null);
      setUserEnteredOption(null);
      if (onSelectionChange) {
        onSelectionChange(null);
      }
      return;
    }

    if (!stringOptions.includes(newValue) && freeSolo) {
      setUserEnteredOption(newValue);
    }

    if (newValue !== value) {
      onChange(newValue);

      // Find the full object that matches the selected value
      if (onSelectionChange) {
        const allData = [
          ...options1,
          ...options2,
          ...initialValueOptions1,
          ...initialValueOptions2,
        ];
        const selectedObject =
          allData.find((item) => String(item[fieldName]) === newValue) || null;
        onSelectionChange(selectedObject);
      }
    }
  };

  const isLoading =
    isFetchingOptions1 ||
    isFetchingOptions2 ||
    isFetchingInitialValueOptions1 ||
    isFetchingInitialValueOptions2;

  return (
    <Autocomplete
      sx={sx}
      open={open}
      value={value || null}
      multiple={false}
      freeSolo={freeSolo}
      selectOnFocus={freeSolo}
      clearOnBlur={freeSolo}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      filterOptions={(optionList, params) => {
        const filtered = filter(optionList, params);

        if (
          freeSolo &&
          params.inputValue !== '' &&
          !optionList.includes(params.inputValue)
        ) {
          filtered.push(params.inputValue);
        }

        return filtered;
      }}
      onChange={handleChange}
      options={allOptions}
      getOptionLabel={getOptionLabel || undefined}
      loading={isLoading}
      noOptionsText={`No ${label} found`}
      renderInput={(params) => (
        <Box sx={{ mb }}>
          <TextField
            inputRef={ref}
            {...params}
            onBlur={(e) => {
              onBlur();
              additionalOnBlur && additionalOnBlur(e);
            }}
            onChange={(e) => {
              if (params.inputProps.onChange)
                params.inputProps.onChange(e as ChangeEvent<HTMLInputElement>);
              debouncedSetSearchQuery(e.target.value);
            }}
            label={label}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              startAdornment: showAiIcon ? (
                <IconSparkles color={theme.palette.success.main} size={20} />
              ) : null,
            }}
          />
          {errors?.[name]?.message && (
            <FormHelperText error id={`${name}Error`}>
              {errors?.[name]?.message}
            </FormHelperText>
          )}
        </Box>
      )}
      disabled={disabled}
      fullWidth={fullWidth}
      disableClearable={disableClearable}
    />
  );
};
